export const reccImplementationDataPart1 = {
  "2.1233": {
    title: "Analyse flue gas for proper air:fuel ratio",
    description: "Optimize combustion efficiency by analyzing flue gas to maintain proper air/fuel balance, reducing excess air without risking incomplete combustion.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Install in-situ or extractive Oâ‚‚ and CO analyzers (and NOx where relevant)",
          "Measure stack temperature and draft",
          "Provide validation ports for calibration checks"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Implement oxygen trim controls to automatically adjust airflow",
          "Use cross-limited airâ€“fuel ratio control for safety",
          "Apply load-indexed curves and bias limits for stable operation"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Tune burners across the full load range",
          "Carry out seasonal retuning (adjust for weather/ambient changes)",
          "Maintain gasket and viewport integrity"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Clean burners and balance registers for even combustion",
          "Seal leaks in ductwork and furnaces",
          "Align flame scanners correctly"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Track efficiency curves against excess Oâ‚‚ levels",
          "Monitor analyzer drift and recalibrate when needed",
          "Rationalize alarms to focus on significant CO excursions"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Routine burner tuning", "Leak sealing", "Enforce standard operating procedures"],
      mediumCost: ["Install Oâ‚‚/CO analyzers with basic trim control", "Add variable speed drives to combustion fans"],
      highCost: ["Advanced or model predictive combustion control", "Upgrade burners and registers"]
    },
    riskFactors: ["Low Oâ‚‚ levels risk CO build-up and flameouts", "Fuel variability requires adaptive systems", "Emission standards may require minimum excess air"]
  },
  "2.2136": {
    title: "Install and repair insulation on steam lines",
    description: "Reduce heat losses from steam distribution systems by installing proper insulation and repairing damaged sections.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Conduct thermal imaging surveys to identify hot spots",
          "Measure surface temperatures of uninsulated lines",
          "Calculate heat loss rates and energy costs"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install insulation jackets with removable sections for maintenance",
          "Use proper insulation materials rated for steam temperatures",
          "Implement vapor barriers to prevent moisture infiltration"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Establish insulation inspection schedules",
          "Train maintenance staff on proper insulation techniques",
          "Prioritize insulation repairs based on heat loss calculations"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Replace damaged or missing insulation sections",
          "Seal joints and penetrations properly",
          "Use weather-resistant outer coverings"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Track steam consumption and distribution losses",
          "Monitor insulation performance over time",
          "Document insulation condition in CMMS system"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Repair damaged insulation sections", "Seal minor gaps and joints"],
      mediumCost: ["Install new insulation on uninsulated lines", "Replace deteriorated insulation systems"],
      highCost: ["Complete reinsulation of entire steam distribution system"]
    },
    riskFactors: ["High surface temperatures during installation", "Asbestos in old insulation requires special handling", "Steam leaks during maintenance work"]
  },
  "2.2414": {
    title: "Use waste heat from hot flue gases to preheat",
    description: "Recover waste heat from flue gases using heat exchangers to preheat combustion air, feedwater, or process fluids.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Measure flue gas temperatures and flow rates",
          "Calculate available waste heat recovery potential",
          "Monitor heat exchanger performance and efficiency"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install heat recovery heat exchangers (air preheaters, economizers)",
          "Implement bypass controls for maintenance and startup",
          "Add temperature controls to prevent overheating"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Optimize heat recovery system operation",
          "Balance heat recovery with process requirements",
          "Train operators on heat recovery system controls"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Clean heat exchanger surfaces regularly",
          "Inspect for corrosion and fouling",
          "Replace heat exchanger elements as needed"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Monitor heat recovery efficiency continuously",
          "Track fuel savings from preheating",
          "Analyze heat exchanger performance trends"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Optimize existing heat recovery operation", "Improve heat exchanger maintenance"],
      mediumCost: ["Install air preheaters", "Add economizers to boilers"],
      highCost: ["Complete waste heat recovery systems with multiple heat exchangers"]
    },
    riskFactors: ["Corrosion from condensing flue gases", "Fouling reduces heat transfer efficiency", "High capital cost with long payback periods"]
  },
  "2.2437": {
    title: "Recover waste heat from equipment",
    description: "Capture and utilize waste heat from industrial equipment and processes to reduce overall energy consumption.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Survey equipment for waste heat sources",
          "Measure temperatures and heat rejection rates",
          "Identify potential heat recovery applications"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install heat recovery heat exchangers",
          "Implement heat pump systems where applicable",
          "Add thermal storage systems for load balancing"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Optimize heat recovery system operation",
          "Coordinate heat recovery with process scheduling",
          "Train operators on integrated heat management"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Maintain heat exchanger cleanliness",
          "Inspect heat recovery systems regularly",
          "Optimize heat transfer fluid properties"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Monitor waste heat recovery performance",
          "Track energy savings from heat recovery",
          "Analyze heat balance across facility"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Improve existing heat recovery maintenance", "Optimize heat recovery controls"],
      mediumCost: ["Install heat exchangers for waste heat recovery", "Add heat pumps for low-grade heat recovery"],
      highCost: ["Integrated waste heat recovery systems", "Combined heat and power systems"]
    },
    riskFactors: ["Complex integration with existing systems", "Variable waste heat availability", "High capital investment requirements"]
  },
  "2.2511": {
    title: "Insulate bare equipment",
    description: "Install insulation on uninsulated equipment surfaces to reduce heat losses and improve energy efficiency.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Conduct infrared thermal surveys",
          "Measure surface temperatures of bare equipment",
          "Calculate potential energy savings from insulation"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install proper insulation materials for equipment temperatures",
          "Use removable insulation blankets for maintenance access",
          "Implement personnel protection measures for high-temperature equipment"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Prioritize insulation based on heat loss and safety",
          "Schedule insulation work during maintenance shutdowns",
          "Train personnel on insulated equipment operation"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Install and maintain equipment insulation",
          "Inspect insulation condition regularly",
          "Replace damaged insulation promptly"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Track energy consumption before and after insulation",
          "Monitor equipment surface temperatures",
          "Document insulation maintenance in CMMS"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Insulate small valves and fittings", "Install pipe insulation"],
      mediumCost: ["Insulate tanks and vessels", "Install removable insulation blankets"],
      highCost: ["Complete equipment insulation programs", "High-temperature refractory insulation"]
    },
    riskFactors: ["High surface temperatures during installation", "Access limitations for equipment insulation", "Personnel safety during installation"]
  }
};


